import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from 'recharts';
import { 
  LayoutDashboard, Users, Calendar, Download, LogOut, 
  Search, Filter, Eye, Edit, Trash2, AlertTriangle, 
  CheckCircle, AlertCircle, TrendingUp, Loader2, RefreshCw
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_teamcraft-app-2/artifacts/5lqryo0g_logo%20banner%20grey.jpg';

const AdminDashboard = () => {
  const { t } = useLanguage();
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [filters, setFilters] = useState({ status: '', risk_level: '', tag: '' });
  const [searchQuery, setSearchQuery] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({ status: '', tag: '', notes: '' });

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    fetchData();
  }, [filters]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams();
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.risk_level) queryParams.append('risk_level', filters.risk_level);
      if (filters.tag) queryParams.append('tag', filters.tag);

      const [leadsRes, statsRes, bookingsRes] = await Promise.all([
        axios.get(`${API}/leads?${queryParams}`, authHeaders),
        axios.get(`${API}/stats`, authHeaders),
        axios.get(`${API}/bookings`, authHeaders)
      ]);

      setLeads(leadsRes.data);
      setStats(statsRes.data);
      setBookings(bookingsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401) {
        logout();
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await axios.get(`${API}/leads/export/csv`, {
        ...authHeaders,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'teamcraft_leads.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Export error:', error);
    }
  };

  const handleUpdateLead = async () => {
    if (!selectedLead) return;
    try {
      await axios.patch(`${API}/leads/${selectedLead.id}`, editForm, authHeaders);
      setEditDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Update error:', error);
    }
  };

  const handleDeleteLead = async (leadId) => {
    if (!window.confirm('Are you sure you want to delete this lead?')) return;
    try {
      await axios.delete(`${API}/leads/${leadId}`, authHeaders);
      fetchData();
    } catch (error) {
      console.error('Delete error:', error);
    }
  };

  const openEditDialog = (lead) => {
    setSelectedLead(lead);
    setEditForm({
      status: lead.status || '',
      tag: lead.tag || '',
      notes: lead.notes || ''
    });
    setEditDialogOpen(true);
  };

  const filteredLeads = leads.filter(lead => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      lead.name?.toLowerCase().includes(query) ||
      lead.company?.toLowerCase().includes(query) ||
      lead.email?.toLowerCase().includes(query)
    );
  });

  const getRiskBadge = (risk) => {
    const variants = {
      high: 'bg-red-100 text-red-800',
      moderate: 'bg-yellow-100 text-yellow-800',
      low: 'bg-green-100 text-green-800'
    };
    return variants[risk] || 'bg-gray-100 text-gray-800';
  };

  const getStatusBadge = (status) => {
    const variants = {
      new: 'bg-blue-100 text-blue-800',
      contacted: 'bg-yellow-100 text-yellow-800',
      proposal_sent: 'bg-purple-100 text-purple-800',
      won: 'bg-green-100 text-green-800',
      lost: 'bg-red-100 text-red-800'
    };
    return variants[status] || 'bg-gray-100 text-gray-800';
  };

  const COLORS = ['#1A1A1A', '#C5A059', '#64748B', '#10B981', '#EF4444'];

  const statusData = stats ? Object.entries(stats.status_distribution).map(([name, value]) => ({ name, value })) : [];
  const serviceData = stats ? Object.entries(stats.service_distribution).map(([name, value]) => ({ name: name.split(' ')[0], value })) : [];

  const navItems = [
    { id: 'dashboard', icon: LayoutDashboard, label: t('admin.dashboard') },
    { id: 'leads', icon: Users, label: t('admin.leads') },
    { id: 'bookings', icon: Calendar, label: t('admin.bookings') }
  ];

  return (
    <div className="min-h-screen flex" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="admin-sidebar w-64 flex-shrink-0 p-6" data-testid="admin-sidebar">
        <div className="mb-10">
          <img src={LOGO_URL} alt="TeamCraft" className="h-10 brightness-0 invert" />
        </div>

        <nav className="space-y-2">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`admin-nav-item w-full ${activeTab === item.id ? 'active' : ''}`}
              data-testid={`nav-${item.id}`}
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </button>
          ))}
        </nav>

        <div className="mt-auto pt-8 border-t border-white/10 mt-8">
          <div className="text-gray-400 text-sm mb-4">
            {user?.email}
          </div>
          <button
            onClick={() => { logout(); navigate('/'); }}
            className="admin-nav-item w-full text-red-400 hover:text-red-300"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" />
            {t('nav.logout')}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 bg-gray-50 p-8 overflow-auto">
        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div className="space-y-8" data-testid="dashboard-content">
            <div className="flex items-center justify-between">
              <h1 className="text-3xl font-bold text-[#1A1A1A]">{t('admin.dashboard')}</h1>
              <Button onClick={fetchData} variant="outline" size="sm" className="flex items-center gap-2">
                <RefreshCw className="w-4 h-4" />
                Refresh
              </Button>
            </div>

            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-[#C5A059]" />
              </div>
            ) : stats && (
              <>
                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <div className="bg-white p-6 border border-gray-200" data-testid="stat-total-leads">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-[#1A1A1A] flex items-center justify-center">
                        <Users className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">{t('admin.totalLeads')}</p>
                        <p className="text-3xl font-bold text-[#1A1A1A] font-mono">{stats.total_leads}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white p-6 border border-gray-200" data-testid="stat-new-leads">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-blue-500 flex items-center justify-center">
                        <AlertCircle className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">{t('admin.newLeads')}</p>
                        <p className="text-3xl font-bold text-[#1A1A1A] font-mono">{stats.new_leads}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white p-6 border border-gray-200" data-testid="stat-high-risk">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-red-500 flex items-center justify-center">
                        <AlertTriangle className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">{t('admin.highRisk')}</p>
                        <p className="text-3xl font-bold text-[#1A1A1A] font-mono">{stats.high_risk}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white p-6 border border-gray-200" data-testid="stat-won-deals">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-green-500 flex items-center justify-center">
                        <TrendingUp className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">{t('admin.conversion')}</p>
                        <p className="text-3xl font-bold text-[#1A1A1A] font-mono">{stats.won_deals}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Charts */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-white p-6 border border-gray-200">
                    <h3 className="text-lg font-semibold text-[#1A1A1A] mb-4">Lead Status Distribution</h3>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={statusData}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={80}
                            paddingAngle={2}
                            dataKey="value"
                          >
                            {statusData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="flex flex-wrap gap-3 mt-4">
                      {statusData.map((item, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm">
                          <div className="w-3 h-3" style={{ backgroundColor: COLORS[idx % COLORS.length] }}></div>
                          <span className="capitalize">{item.name.replace('_', ' ')}: {item.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-white p-6 border border-gray-200">
                    <h3 className="text-lg font-semibold text-[#1A1A1A] mb-4">Service Recommendations</h3>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={serviceData}>
                          <XAxis dataKey="name" />
                          <YAxis />
                          <Tooltip />
                          <Bar dataKey="value" fill="#C5A059" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Leads Tab */}
        {activeTab === 'leads' && (
          <div className="space-y-6" data-testid="leads-content">
            <div className="flex items-center justify-between">
              <h1 className="text-3xl font-bold text-[#1A1A1A]">{t('admin.leads')}</h1>
              <Button onClick={handleExportCSV} className="flex items-center gap-2" data-testid="export-csv-btn">
                <Download className="w-4 h-4" />
                {t('admin.export')}
              </Button>
            </div>

            {/* Filters */}
            <div className="bg-white p-4 border border-gray-200 flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Search className="w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search leads..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-64"
                  data-testid="search-input"
                />
              </div>

              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-400" />
                <Select value={filters.status} onValueChange={(v) => setFilters(prev => ({ ...prev, status: v }))}>
                  <SelectTrigger className="w-40" data-testid="filter-status">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">All Status</SelectItem>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="contacted">Contacted</SelectItem>
                    <SelectItem value="proposal_sent">Proposal Sent</SelectItem>
                    <SelectItem value="won">Won</SelectItem>
                    <SelectItem value="lost">Lost</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={filters.risk_level} onValueChange={(v) => setFilters(prev => ({ ...prev, risk_level: v }))}>
                  <SelectTrigger className="w-40" data-testid="filter-risk">
                    <SelectValue placeholder="Risk Level" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">All Risk</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="moderate">Moderate</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={filters.tag} onValueChange={(v) => setFilters(prev => ({ ...prev, tag: v }))}>
                  <SelectTrigger className="w-40" data-testid="filter-tag">
                    <SelectValue placeholder="Tag" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">All Tags</SelectItem>
                    <SelectItem value="recruitment">Recruitment</SelectItem>
                    <SelectItem value="team">Team</SelectItem>
                    <SelectItem value="transformation">Transformation</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Leads Table */}
            <div className="bg-white border border-gray-200 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Company</TableHead>
                    <TableHead>Risk</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Score</TableHead>
                    <TableHead>Service</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                      </TableCell>
                    </TableRow>
                  ) : filteredLeads.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                        No leads found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredLeads.map((lead) => (
                      <TableRow key={lead.id} data-testid={`lead-row-${lead.id}`}>
                        <TableCell className="font-medium">{lead.name}</TableCell>
                        <TableCell>{lead.company}</TableCell>
                        <TableCell>
                          <Badge className={`${getRiskBadge(lead.risk_level)} capitalize`}>
                            {lead.risk_level}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={`${getStatusBadge(lead.status)} capitalize`}>
                            {lead.status?.replace('_', ' ')}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono">{lead.total_score}/30</TableCell>
                        <TableCell className="text-sm">{lead.recommended_service}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Link to={`/results/${lead.id}`}>
                              <Button variant="ghost" size="sm" data-testid={`view-lead-${lead.id}`}>
                                <Eye className="w-4 h-4" />
                              </Button>
                            </Link>
                            <Button variant="ghost" size="sm" onClick={() => openEditDialog(lead)} data-testid={`edit-lead-${lead.id}`}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteLead(lead.id)} data-testid={`delete-lead-${lead.id}`}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {/* Bookings Tab */}
        {activeTab === 'bookings' && (
          <div className="space-y-6" data-testid="bookings-content">
            <h1 className="text-3xl font-bold text-[#1A1A1A]">{t('admin.bookings')}</h1>

            <div className="bg-white border border-gray-200 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Slot</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Booked At</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                      </TableCell>
                    </TableRow>
                  ) : bookings.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                        No bookings yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    bookings.map((booking) => (
                      <TableRow key={booking.id}>
                        <TableCell className="font-medium">{booking.name}</TableCell>
                        <TableCell>{booking.email}</TableCell>
                        <TableCell className="font-mono">{booking.slot_id}</TableCell>
                        <TableCell>
                          <Badge className="bg-green-100 text-green-800 capitalize">{booking.status}</Badge>
                        </TableCell>
                        <TableCell className="text-sm text-gray-500">
                          {new Date(booking.created_at).toLocaleDateString()}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </main>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Lead: {selectedLead?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Status</label>
              <Select value={editForm.status} onValueChange={(v) => setEditForm(prev => ({ ...prev, status: v }))}>
                <SelectTrigger data-testid="edit-status">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="contacted">Contacted</SelectItem>
                  <SelectItem value="proposal_sent">Proposal Sent</SelectItem>
                  <SelectItem value="won">Won</SelectItem>
                  <SelectItem value="lost">Lost</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Tag</label>
              <Select value={editForm.tag} onValueChange={(v) => setEditForm(prev => ({ ...prev, tag: v }))}>
                <SelectTrigger data-testid="edit-tag">
                  <SelectValue placeholder="Select tag" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="recruitment">Recruitment</SelectItem>
                  <SelectItem value="team">Team</SelectItem>
                  <SelectItem value="transformation">Transformation</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Notes</label>
              <Textarea
                value={editForm.notes}
                onChange={(e) => setEditForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Add notes..."
                rows={4}
                data-testid="edit-notes"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleUpdateLead} className="btn-primary" data-testid="save-lead-btn">
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
